import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  InlineAskUserCard,
  type InlineAskUserArgs,
} from './inline-ask-user-card';

/**
 * Bridges agent `ask_user` (HITL) questions from the message stream to the
 * composer. The assistant message synthesizes a `__ask_user` part and registers
 * it here while pending; `ComposerAskUserPanel` (pinned directly above the
 * message input) renders the interactive card — matching the composer-attached
 * pattern rather than burying the question in the thread.
 */

interface PendingAskUserContextValue {
  register: (id: string, args: InlineAskUserArgs) => void;
  unregister: (id: string) => void;
  /**
   * Optimistically hide a question the user just answered/dismissed. Sticky:
   * the underlying `__ask_user` part stays `pending` until a later run event
   * flips it, and any remount/re-render in between would otherwise re-register
   * the still-pending question and reopen the card (the flicker). Marking it
   * resolved keeps it out of `pending` regardless of re-registration; the
   * cleanup `unregister` prunes it once the part actually resolves.
   */
  resolve: (id: string) => void;
  /** Ordered pending questions (registration order; most recent last). */
  pending: Array<{ id: string; args: InlineAskUserArgs }>;
}

const PendingAskUserContext = createContext<PendingAskUserContextValue | null>(
  null,
);

export function PendingAskUserProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [entries, setEntries] = useState<Map<string, InlineAskUserArgs>>(
    () => new Map(),
  );
  // Ids the user has answered/dismissed this session — filtered out of
  // `pending` so a re-registered still-pending question can't reopen the card.
  const [resolved, setResolved] = useState<Set<string>>(() => new Set());

  const register = useCallback((id: string, args: InlineAskUserArgs) => {
    setEntries((prev) => {
      const next = new Map(prev);
      next.set(id, args);
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    // The part has genuinely resolved (or unmounted) — drop the sticky flag so
    // the set doesn't grow unbounded.
    setResolved((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const resolve = useCallback((id: string) => {
    setResolved((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const pending = useMemo(
    () =>
      Array.from(entries, ([id, args]) => ({ id, args })).filter(
        ({ id }) => !resolved.has(id),
      ),
    [entries, resolved],
  );

  const value = useMemo<PendingAskUserContextValue>(
    () => ({ register, unregister, resolve, pending }),
    [register, unregister, resolve, pending],
  );

  return (
    <PendingAskUserContext.Provider value={value}>
      {children}
    </PendingAskUserContext.Provider>
  );
}

/**
 * Registers a pending ask-user question (no UI). Called by the assistant
 * message for each `__ask_user` part. No-op outside a provider, or once the
 * request is no longer pending.
 */
export function useRegisterPendingAskUser(
  args: InlineAskUserArgs,
  isPending: boolean,
): void {
  const ctx = useContext(PendingAskUserContext);
  // Pull the stable callbacks out of the context. `register`/`unregister` keep
  // their identity across re-renders (useCallback with no deps), whereas `ctx`
  // itself is a fresh object every time the pending map changes. Depending on
  // `ctx` here would re-run this effect whenever ANY question is (un)registered
  // — including the unregister fired by `onResolved` after the user answers —
  // and immediately re-register this still-"pending" question, reopening the
  // card and interrupting its fade-out.
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const id = args.askUserRequestId || args.inboxItemId || '';
  // Re-register when identity or status changes; `register` always stores the
  // latest args (questions are immutable per request, so this is cheap).
  useEffect(() => {
    if (!register || !unregister || !id || !isPending) return;
    register(id, args);
    return () => unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, id, isPending, args.status]);
}

/** Tiny render-less component so the registration hook can be mapped per part. */
export function AskUserRegistration({ args }: { args: InlineAskUserArgs }) {
  useRegisterPendingAskUser(args, (args.status ?? 'pending') === 'pending');
  return null;
}

/**
 * Panel pinned above the composer that renders the interactive card for each
 * pending ask-user question. Renders nothing when none are pending (or outside
 * a provider).
 */
export function ComposerAskUserPanel() {
  const ctx = useContext(PendingAskUserContext);
  if (!ctx || ctx.pending.length === 0) return null;

  return (
    // `relative z-10` lifts the card above the footer's fade-gradient pseudo
    // (`.aui-thread-viewport-footer::before`), which otherwise paints a solid
    // background band over the card's top border.
    <div className="relative z-10 mb-2 w-full">
      <AnimatePresence initial={false}>
        {ctx.pending.map(({ id, args }) => (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <InlineAskUserCard
              args={args}
              attached
              onResolved={() => ctx.resolve(id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
